import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rulesApi, glossaryApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import './GlossaryAdmin.css';

function GlossaryAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [terms, setTerms] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    term: '',
    def: '',
    rec_id: '',
  });

  const formatRuleName = (rule) => {
    if (!rule) return '';
    if (rule.number) return `${rule.number} - ${rule.description || rule.gr_desc || ''}`;
    const num = rule.gr_num ?? 0;
    const sub = rule.gr_sub ?? 0;
    const desc = rule.gr_desc || rule.title || '';
    const number = sub && sub !== 0 && sub !== '0' ? `${num}.${sub}` : `${num}`;
    return `${number} - ${desc}`;
  };

  const applySearchFilter = useCallback((items, term) => {
    const lower = term.toLowerCase().trim();
    const filtered = items.filter((t) => {
      const termText = (t.term || '').toLowerCase();
      const defText = (t.def || '').toLowerCase();
      const idText = String(t.term_id || '');
      const redId = String(t.rec_id || '');
      const ruleRef = rules.find((r) => (r.id || r.gr_id) === t.rec_id);
      const ruleName = ruleRef ? formatRuleName(ruleRef).toLowerCase() : '';
      return (
        termText.includes(lower) ||
        defText.includes(lower) ||
        idText.includes(lower) ||
        redId.includes(lower) ||
        ruleName.includes(lower)
      );
    });
    setFilteredTerms(filtered);
  }, [rules]);

  const loadTerms = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await glossaryApi.adminGetAll();
      if (response.data.success) {
        const items = response.data.data || [];
        setTerms(items);
        // Don't apply search filter here - let the searchTerm effect handle it
        setFilteredTerms(items);
      } else {
        setError(response.data.message || 'Failed to load glossary terms');
      }
    } catch (err) {
      console.error('Error loading glossary terms:', err);
      setError(err.response?.data?.message || 'Failed to load glossary terms');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - only load once

  const loadRules = useCallback(async () => {
    try {
      const response = await rulesApi.getAll(1, 1000);
      if (response.data.success) {
        const rulesData = Array.isArray(response.data.data) ? response.data.data : [];
        setRules(rulesData);
      }
    } catch (err) {
      console.error('Error loading rules for glossary:', err);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.profile !== 'admin') {
        navigate('/profile');
        return;
      }
      loadRules();
      loadTerms();
    }
  }, [user, authLoading, navigate, loadRules, loadTerms]);

  // Apply search filter when searchTerm or terms change
  useEffect(() => {
    if (terms.length > 0) {
      if (searchTerm.trim()) {
        applySearchFilter(terms, searchTerm);
      } else {
        setFilteredTerms(terms);
      }
    }
  }, [searchTerm, terms, applySearchFilter]);

  const handleCreate = () => {
    setEditingTerm(null);
    setFormData({ term: '', def: '', rec_id: '' });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (item) => {
    console.log('Edit clicked for item:', item);
    if (!item || !item.term_id) {
      console.error('Invalid item for edit:', item);
      setError('Invalid item selected for editing');
      return;
    }
    setEditingTerm(item);
    setFormData({
      term: item.term || '',
      def: item.def || '',
      rec_id: item.rec_id ? String(item.rec_id) : '',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.getElementById('glossary-form') || document.querySelector('.admin-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Also focus the first input
        const firstInput = formElement.querySelector('input, textarea, select');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 150);
  };

  const handleDeleteClick = (item) => {
    setTermToDelete(item);
    setShowDeleteModal(true);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (term.trim()) {
      applySearchFilter(terms, term);
    } else {
      setFilteredTerms(terms);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredTerms(terms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.term || !formData.def) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setError('');
      setSuccess('');
      const payload = {
        term: formData.term,
        def: formData.def,
      };
      if (formData.rec_id) {
        payload.rec_id = parseInt(formData.rec_id, 10);
      }
      if (editingTerm) {
        await glossaryApi.adminUpdate(editingTerm.term_id, payload);
        setSuccess('Term updated successfully');
      } else {
        await glossaryApi.adminCreate(payload);
        setSuccess('Term created successfully');
      }
      setShowForm(false);
      setEditingTerm(null);
      setSearchTerm('');
      loadTerms();
    } catch (err) {
      console.error('Error saving glossary term:', err);
      setError(err.response?.data?.message || 'Failed to save glossary term');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!termToDelete) return;
    try {
      setError('');
      await glossaryApi.adminDelete(termToDelete.term_id);
      setSuccess('Term deleted successfully');
      setShowDeleteModal(false);
      setTermToDelete(null);
      setSearchTerm('');
      loadTerms();
    } catch (err) {
      console.error('Error deleting glossary term:', err);
      setError(err.response?.data?.message || 'Failed to delete glossary term');
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTerm(null);
    setFormData({ term: '', def: '', rec_id: '' });
    setError('');
    setSuccess('');
  };

  if (authLoading || loading) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="glossary-admin">
          <div className="admin-header">
            <h1>Glossary Admin</h1>
            <Link to="/admin" className="btn btn-secondary back-btn">
              <i className="bi bi-arrow-left"></i> Back to GR Admin
            </Link>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="admin-actions">
            <button onClick={handleCreate} className="btn btn-primary">
              <i className="bi bi-plus-circle"></i> Create New Term
            </button>
            <div className="admin-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search glossary by term, definition, or rule..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="admin-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="admin-search-clear"
                  title="Clear search"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              )}
            </div>
          </div>

          {searchTerm && (
            <div className="admin-search-results">
              Showing {filteredTerms.length} of {terms.length} terms
            </div>
          )}

          {showForm && (
            <div className="admin-form" id="glossary-form">
              <h2>{editingTerm ? 'Edit Term' : 'Create New Term'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="term">Term *</label>
                  <input
                    type="text"
                    id="term"
                    name="term"
                    value={formData.term}
                    onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                    required
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="def">Definition *</label>
                  <textarea
                    id="def"
                    name="def"
                    value={formData.def}
                    onChange={(e) => setFormData({ ...formData, def: e.target.value })}
                    required
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rec_id">Rule Reference (Optional)</label>
                  <select
                    id="rec_id"
                    name="rec_id"
                    value={formData.rec_id}
                    onChange={(e) => setFormData({ ...formData, rec_id: e.target.value })}
                  >
                    <option value="">-- Select a Rule --</option>
                    {rules.map((rule) => {
                      const ruleId = rule.id || rule.gr_id;
                      return (
                        <option key={ruleId} value={ruleId}>
                          {formatRuleName(rule)}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingTerm ? 'Update Term' : 'Create Term'}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="admin-table-container">
            <table className="admin-table">
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '42%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Term</th>
                  <th>Rule Ref</th>
                  <th>Definition</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(searchTerm ? filteredTerms : terms).length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {searchTerm ? `No terms found matching "${searchTerm}"` : 'No terms found'}
                    </td>
                  </tr>
                ) : (
                  (searchTerm ? filteredTerms : terms).map((item) => {
                    const ruleRef = rules.find((r) => (r.id || r.gr_id) === item.rec_id);
                    return (
                      <tr key={item.term_id}>
                        <td>{item.term_id}</td>
                        <td className="term-text">{item.term}</td>
                        <td>{ruleRef ? formatRuleName(ruleRef) : item.rec_id || '-'}</td>
                        <td className="definition-text">{item.def || ''}</td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                            className="btn btn-sm btn-secondary"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteClick(item);
                            }}
                            className="btn btn-sm btn-danger"
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />

      <ConfirmModal
        show={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setTermToDelete(null);
        }}
        title="Delete Term"
        message={termToDelete ? `Are you sure you want to delete "${termToDelete.term}"?` : 'Are you sure you want to delete this term?'}
      />
    </>
  );
}

export default GlossaryAdmin;
